import { useState } from 'react';
import { Box, Chip, IconButton, ListItemText, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useAIChatContext } from '../context/AIChatContext';
import { useAssistantRoles } from '../hooks/useAssistantRoles';

export function AIChatSessionList() {
  const { sessions, activeSessionId, setActiveSessionId, removeSession } = useAIChatContext();
  const { roles } = useAssistantRoles();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const activeSession = sessions.find((s) => s.id === activeSessionId);
  const roleName = (assistantRoleKey?: string | null) =>
    assistantRoleKey ? roles.find((r) => r.key === assistantRoleKey)?.name : undefined;

  return (
    <>
      <Box
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          cursor: 'pointer',
          borderRadius: 1,
          px: 0.75,
          py: 0.25,
          minWidth: 0,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 190 }}>
          {activeSession?.title || 'New chat'}
        </Typography>
        {roleName(activeSession?.assistantRoleKey) && (
          <Chip label={roleName(activeSession?.assistantRoleKey)} size="small" sx={{ height: 18, fontSize: 10 }} />
        )}
        <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
      </Box>

      <Menu
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 260, maxHeight: 360 } } }}
      >
        {sessions.length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">No chats yet</Typography>
          </MenuItem>
        )}
        {sessions.map((s) => (
          <MenuItem
            key={s.id}
            selected={s.id === activeSessionId}
            onClick={() => {
              setActiveSessionId(s.id);
              setAnchor(null);
            }}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Stack sx={{ minWidth: 0, flex: 1 }} spacing={0.25}>
              <ListItemText
                primary={s.title || 'New chat'}
                primaryTypographyProps={{ noWrap: true, fontSize: 13, fontWeight: s.id === activeSessionId ? 600 : 400 }}
                sx={{ m: 0 }}
              />
              {roleName(s.assistantRoleKey) && (
                <Chip
                  label={roleName(s.assistantRoleKey)}
                  size="small"
                  sx={{ height: 16, fontSize: 9, alignSelf: 'flex-start' }}
                />
              )}
            </Stack>
            <Tooltip title="Delete chat">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  void removeSession(s.id);
                }}
              >
                <DeleteOutlineIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
